import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { generateValidation } from "../ContactUsForm/validations";
import ReCaptcha from "../ContactUsForm/ReCaptcha";
import { warrantyFormServerAPI } from "@/services/formSubmitAPI/client";
import { capitalizeFirstLetter } from "@/utils/formatters";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";
import { useLocale } from "@/hooks/useLocale";
import { useRouter } from "next/navigation";

const captchaDisabled = process.env.NEXT_PUBLIC_RECAPTCHA_DISABLE === "0";
const countryForm = `${capitalizeFirstLetter(process.env.NEXT_PUBLIC_COUNTRY_NAME)} - Forms`;
const optionalFieldNames = new Set(["first_name", "last_name"]);

function inputClass(name, errors, touched) {
  const base = "w-full border rounded px-3 py-2 text-[14px]";

  return errors[name] && touched[name]
    ? `${base} border-red-500`
    : `${base} border-slate-300`;
}

function ariaDescribedBy(name, errors, touched) {
  return errors[name] && touched[name] ? `${name}-error` : undefined;
}

const WarrantyRegistration = ({
  formConfig = {},
  warrantyFormConfigurationsData = {},
}) => {
  const router = useRouter();
  const locale = useLocale();

  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const contactFormConfigurations =
    warrantyFormConfigurationsData?.items?.[0]?.fields?.configurations?.filter(
      config => config?.fields?.internalTitle === countryForm
    ) || [];

  const emailConfig = contactFormConfigurations?.[0]?.fields?.config;

  const formLink = "warranty-registration";

  const [formTemplate, storeRecipient] =
    Object.entries(emailConfig ?? {}).find(([key]) => key === formLink) || [];

  const { successText } = useContentfulTranslations();

  const {
    formFields = [],
    heading = "",
    submit = "Submit",
    submitting = "Submitting",
    selectList = "Select",
    capchaErrorMessage = "Please verify the captcha",
    capchaVerificationMessage = "Captcha verification failed",
    tryAgainMessage = "Something went wrong. Please try again.",
    buttonUrl = "",
  } = formConfig;

  const captchaField = {
    name: "captchaToken",
    type: "captcha",
    required: true,
    message: capchaErrorMessage,
  };

  const warrantyField = formFields.find(
    field => field.warrantyText && field.warrantyUrl
  );

  const warrantyText = warrantyField?.warrantyText || "";
  const warrantyUrl = warrantyField?.warrantyUrl || "";

  const normalizedFormFields = React.useMemo(
    () =>
      formFields.map(field =>
        optionalFieldNames.has(field.name)
          ? {
              ...field,
              required: false,
            }
          : field
      ),
    [formFields]
  );

  const initialValues = React.useMemo(
    () =>
      normalizedFormFields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: "",
        }),
        {
          captchaToken: "",
        }
      ),
    [normalizedFormFields]
  );

  const validationSchema = React.useMemo(
    () =>
      generateValidation([
        ...normalizedFormFields,
        ...(captchaDisabled ? [] : [captchaField]),
      ]),
    [normalizedFormFields, capchaErrorMessage]
  );

  async function handleSubmit(
    values,
    { setSubmitting, setFieldError, resetForm }
  ) {
    setSubmitError(null);

    try {
      const fieldNames = new Set(normalizedFormFields.map(field => field.name));

      const payload = {
        ...Object.fromEntries(
          Object.entries(values).filter(([key]) => fieldNames.has(key))
        ),
        ...(buttonUrl != null && { buttonLink: buttonUrl }),
        formTemplate,
        storeRecipient,
      };

      if (captchaDisabled) {
        const res = await warrantyFormServerAPI(values.captchaToken, {
          values: payload,
        });

        if (res?.message) {
          resetForm();
          setSuccess(true);
        }

        setSubmitting(false);
        return;
      }

      if (!values.captchaToken) {
        setFieldError("captchaToken", capchaErrorMessage);

        setSubmitting(false);
        return;
      }

      const res = await warrantyFormServerAPI(values.captchaToken, {
        values: payload,
        // filteredContactFormConfigurations,
      });

      if (res?.message) {
        resetForm();
        setSuccess(true);
        router.push("/thankyou-warranty");
      } else {
        setFieldError("captchaToken", capchaVerificationMessage);

        setSubmitting(false);
        return;
      }
    } catch (err) {
      console.error("Warranty form submission error:", err);

      setSubmitError(tryAgainMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-[980px] px-4 py-[50px]">
      <h2 className="text-center text-[45px] md:text-[40px] font-[Helvetica-Light,_Helvetica,_sans-serif] text-[#232323]">
        {heading}
      </h2>

      <div className="ml-20 text-red-500 text-[15px]">
        <span aria-hidden="true">*</span> Required
      </div>

      <hr className="mt-3 mb-8 border-t border-black-300" />

      {success ? (
        <div className="flex justify-center items-center">
          <div className="w-full max-w-md text-center">
            <h3 className="text-lg font-semibold !text-[21px] text-gray-800">
              <span
                className="font-[HelveticaRoman,_Helvetica,_sans-serif]"
                dangerouslySetInnerHTML={{
                  __html: successText?.[locale],
                }}
              />
            </h3>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            submitCount,
            isSubmitting,
          }) => (
            <Form className="space-y-5 w-3/4 mx-auto" noValidate>
              {normalizedFormFields.map(field => (
                <div
                  key={field.name}
                  className="grid gap-2 md:gap-4 items-start md:grid-cols-[260px_minmax(0,328px)]"
                >
                  <div className="text-[14px] font-['HelveticaBold',Helvetica,sans-serif] text-slate-700 md:text-right">
                    {field.type !== "checkbox" && field.type !== "captcha" && (
                      <>
                        {field.label}

                        {field.required && (
                          <span
                            aria-hidden="true"
                            className="text-red-500 ml-1 font-bold inline-block"
                          >
                            *
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="max-w-[328px] w-full">
                    {/* ── Select ── */}
                    {field.type === "select" && (
                      <Field
                        as="select"
                        id={field.name}
                        name={field.name}
                        className={inputClass(field.name, errors, touched)}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                      >
                        <option value="">-- {selectList} --</option>

                        {field.options?.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Field>
                    )}

                    {(field.type === "text" || field.type === "email") && (
                      <Field
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        className={inputClass(field.name, errors, touched)}
                        placeholder={field.placeholder || ""}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                        onInput={
                          field.name === "phone"
                            ? e => {
                                e.target.value = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                              }
                            : undefined
                        }
                      />
                    )}

                    {field.type === "date" && (
                      <Field
                        type="text"
                        id={field.name}
                        name={field.name}
                        placeholder={field.placeholder || "dd-mm-yyyy"}
                        className={inputClass(field.name, errors, touched)}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                      />
                    )}

                    {field.type === "textarea" && (
                      <Field
                        as="textarea"
                        id={field.name}
                        name={field.name}
                        rows="5"
                        className={inputClass(field.name, errors, touched)}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                      />
                    )}

                    {field.type === "file" && (
                      <>
                        <input
                          type="file"
                          id={field.name}
                          name={field.name}
                          className="hidden"
                          accept={field.accept}
                          onChange={event => {
                            setFieldValue(
                              field.name,
                              event.target.files?.[0] || null
                            );
                          }}
                        />

                        <label
                          htmlFor={field.name}
                          className="
                            bg-[#dbdbdb]
                            font-[HelveticaBold,_Helvetica,_sans-serif]
                            uppercase
                            inline-flex
                            items-center
                            justify-center
                            mb-0
                            cursor-pointer
                            whitespace-nowrap
                            px-9
                            py-[9px]
                            text-[14px]
                            leading-[1.428571429]
                            rounded-[4px]
                            text-center
                            w-fit
                            max-w-full
                            text-[#232323]
                          "
                          title={values[field.name]?.name || "Upload"}
                        >
                          {values[field.name]?.name || "Upload"}
                        </label>
                      </>
                    )}

                    {field.type === "checkbox" && (
                      <label className="flex items-start gap-2 !text-[14px] !max-w-[558px] !w-full">
                        <Field
                          type="checkbox"
                          name={field.name}
                          className="mt-1"
                        />

                        {field.name === "privacyPolicyConsent" ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: field.label,
                            }}
                          />
                        ) : (
                          <span>{field.label}</span>
                        )}
                      </label>
                    )}

                    {field.type === "checkbox-group" && (
                      <div className="space-y-1">
                        {field.options?.map(option => (
                          <label
                            key={option}
                            className="flex items-center gap-2 text-sm"
                          >
                            <Field
                              type="checkbox"
                              name={field.name}
                              value={option}
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    )}

                    {field.description && (
                      <p className="ml-6 mt-2 text-[14px] font-[HelveticaRoman,_Helvetica,_sans-serif] text-[#232323]">
                        {field.description}
                      </p>
                    )}

                    {field.name !== "captchaToken" &&
                      errors[field.name] &&
                      touched[field.name] && (
                        <p
                          id={`${field.name}-error`}
                          role="alert"
                          className="text-red-600 text-xs mt-1"
                        >
                          {errors[field.name]}
                        </p>
                      )}

                    {/* ── Helper Text ── */}
                    {field.helperText && (
                      <p className="mt-1 text-sm text-[#8e8e8e]">
                        {field.helperText}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {!captchaDisabled && (
                <div className="grid md:grid-cols-[260px_minmax(0,328px)] md:gap-4">
                  <div />

                  <div className="max-w-[328px] w-full">
                    <div
                      className={
                        errors.captchaToken && submitCount > 0
                          ? "[&_iframe]:border-2 [&_iframe]:border-red-500 [&_iframe]:rounded"
                          : ""
                      }
                    >
                      <ReCaptcha
                        siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                        onChange={token => {
                          setFieldValue("captchaToken", token || "");
                        }}
                        onExpired={() => {
                          setFieldValue("captchaToken", "");
                        }}
                        onError={() => {
                          setFieldValue("captchaToken", "");
                        }}
                      />
                    </div>

                    {errors.captchaToken && submitCount > 0 && (
                      <p
                        id="captchaToken-error"
                        role="alert"
                        className="text-red-600 text-xs mt-1"
                      >
                        {errors.captchaToken}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {submitError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="text-red-600 text-sm text-center"
                >
                  {submitError}
                </div>
              )}

              <div className="grid md:grid-cols-[260px_minmax(0,328px)] md:gap-4 mt-2">
                <div />

                <div className="max-w-[328px] w-full">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                      font-[HelveticaBold,Helvetica,_sans-serif]
                      w-full
                      bg-[#3E4A75]
                      text-white
                      py-3
                      uppercase
                      rounded
                      disabled:opacity-60
                      disabled:cursor-not-allowed
                    "
                  >
                    {isSubmitting ? submitting : submit}
                  </button>

                  {warrantyText && warrantyUrl && (
                    <a
                      href={warrantyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-3 mt-3 text-sm text-[#337ab7] !no-underline hover:!no-underline"
                    >
                      {warrantyText}
                    </a>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

const FieldShape = PropTypes.shape({
  name: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.oneOf([
    "text",
    "email",
    "textarea",
    "select",
    "date",
    "file",
    "checkbox",
    "checkbox-group",
    "captcha",
  ]),
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  message: PropTypes.string,
  helperText: PropTypes.string,
  description: PropTypes.string,
  accept: PropTypes.string,
  termsurl: PropTypes.string,
  privacyurl: PropTypes.string,
  warrantyText: PropTypes.string,
  warrantyUrl: PropTypes.string,
});

WarrantyRegistration.propTypes = {
  formConfig: PropTypes.shape({
    heading: PropTypes.string,
    url: PropTypes.string,
    privacyPolicy: PropTypes.string,
    submit: PropTypes.string,
    submitting: PropTypes.string,
    selectList: PropTypes.string,
    capchaErrorMessage: PropTypes.string,
    capchaVerificationMessage: PropTypes.string,
    tryAgainMessage: PropTypes.string,
    formFields: PropTypes.arrayOf(FieldShape),
  }),
  warrantyFormConfigurationsData: PropTypes.object,
};

export default WarrantyRegistration;
